import React, { lazy, Suspense} from 'react';
import { createRef } from 'react';
import  { ProductPage as SourceProductPage }  from 'SourceRoute/ProductPage/ProductPage.component';
import { UPSELL } from 'Store/LinkedProducts/LinkedProducts.reducer';
import Loader from 'Component/Loader/Loader.component';
import ContentWrapper from 'Component/ContentWrapper';
import NoMatchHandler from 'Route/NoMatchHandler';
import TextPlaceholder from 'Component/TextPlaceholder';
import media, { WYSIWYG_MEDIA } from 'Util/Media/Media';
import { appendWithStoreCode } from 'Util/Url';
import Link from 'Component/Link';
import Html from 'Component/Html';
import { PinterestShareButton, TwitterShareButton, FacebookShareButton } from 'react-share'

import {
    PRODUCT_ATTRIBUTE_COMPONENTS,
    PRODUCT_ATTRIBUTE_INSTRUCTIONS,
    PRODUCT_ATTRIBUTE_COMPOSITION,
    PRODUCT_INFORMATION,
    PRODUCT_SHIPPING_RETURN,
    BRAND_DIOR,
    BRAND_CHRISTIAN_DIOR,
    BRAND_CHANEL
} from 'Route/ProductPage/ProductPage.config';
import { ProductType } from 'Type/ProductList';
import ProductActions from 'Component/ProductActions';
import PropTypes from "prop-types";

const reviewsRef = createRef();
const autoRelatedRef = createRef();

export const ProductInformation = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-info" */
    'Component/ProductInformation'
    ));
export const ProductGallery = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-gallery" */
    'Component/ProductGallery'
    ));
export const ProductReviews = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-reviews" */
    'Component/ProductReviews'
    ));
export const ProductAttributes = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-attributes" */
    'Component/ProductAttributes'
    ));
export const ExpandableContent = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-content" */
    'Component/ExpandableContent'
    ));   
export const RecentlyViewedWidget = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-list" */
    'Component/RecentlyViewedWidget'
    ));   
export const ProductLinks = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-list" */
    'Component/ProductLinks'
    ));        
export const CmsBlock = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-content" */
    'Component/CmsBlock'
    ));        
export const ChanelContent = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-content" */
    'Component/ChanelContent'
    ));
export const UniversalIcon = lazy(() => import(
    /* webpackMode: "lazy", webpackChunkName: "icons" */
    'Component/UniversalIcon'
    ));

/** @namespace Route/ProductPage/Component */
export class ProductPage extends SourceProductPage {
    static propTypes = {
        configurableVariantIndex: PropTypes.number.isRequired,
        productOrVariant: ProductType.isRequired,
        getLink: PropTypes.func.isRequired,
        parameters: PropTypes.objectOf(PropTypes.string).isRequired,
        updateConfigurableVariant: PropTypes.func.isRequired,
        dataSource: ProductType.isRequired,
        areDetailsLoaded: PropTypes.bool.isRequired,
        getSelectedCustomizableOptions: PropTypes.func.isRequired,
        setLinkedDownloadables: PropTypes.func.isRequired,
        setLinkedDownloadablesPrice: PropTypes.func.isRequired,
        productOptionsData: PropTypes.object.isRequired,
        setBundlePrice: PropTypes.func.isRequired,
        selectedLinkPrice: PropTypes.number.isRequired,
        selectedBundlePrice: PropTypes.number.isRequired,
        isMobile: PropTypes.bool.isRequired,
        isInformationTabEmpty: PropTypes.bool.isRequired,
        isAttributesTabEmpty: PropTypes.bool.isRequired,
        isComponentsTabEmpty: PropTypes.bool.isRequired,
        isCompositionTabEmpty: PropTypes.bool.isRequired,
        isInstructionsTabEmpty: PropTypes.bool.isRequired,
        selectedBundlePriceExclTax: PropTypes.number.isRequired,
        selectedInitialBundlePrice: PropTypes.number.isRequired
    };

    componentDidUpdate() {
        const {
            dataSource: {
                id
            },
            category,
            brand
        } = this.props;

        if(id && category){
            window.lastVisitedProductId = id;
            window.lastVisitedCategoryId = category?.id;
            window.lastVisitedBrandId = brand?.value;
        }
    }

    tabMap = {
        [PRODUCT_INFORMATION]: {
            name: __('PRODUCT DETAILS'),
            attributeTabName: () => {},
            shouldTabRender: () => {
                const { isInformationTabEmpty, isAttributesTabEmpty } = this.props;

                if(isInformationTabEmpty && isAttributesTabEmpty)
                    return false;

                return true;
            },
            render: (key) => this.renderProductInformationAndAttributesTab(key)
        },
        [PRODUCT_ATTRIBUTE_COMPONENTS]: {
            name: __('COMPONENTS'),
            attributeTabName: () => {
                return this.props?.dataSource?.attributes?.components?.attribute_label;
            },
            shouldTabRender: () => {
                const { isComponentsTabEmpty } = this.props;

                return !isComponentsTabEmpty;
            },
            render: (key) => this.renderProductComponentsTab(key)
        },
        [PRODUCT_ATTRIBUTE_COMPOSITION]: {
            name: __('COMPOSITION'),
            attributeTabName: () => {
                return this.props?.dataSource?.attributes?.composition?.attribute_label;
            },
            shouldTabRender: () => {
                const { isCompositionTabEmpty } = this.props;

                return !isCompositionTabEmpty;
            },
            render: (key) => this.renderProductCompositionTab(key)
        },
        [PRODUCT_ATTRIBUTE_INSTRUCTIONS]: {
            name: __('INSTRUCTIONS FOR USE OF THE PRODUCT'),
            attributeTabName: () => {
                return this.props?.dataSource?.attributes?.instructions?.attribute_label;
            },
            shouldTabRender: () => {
                const { isInstructionsTabEmpty } = this.props;

                return !isInstructionsTabEmpty;
            },
            render: (key) => this.renderProductInstructionsTab(key)
        },
        [PRODUCT_SHIPPING_RETURN]: {
            name: __('SHIPPING & RETURNS'),
            attributeTabName: () => {},
            shouldTabRender: () => true,
            render: (key) => this.renderShippingReturnsTab(key)
        }
    };

    renderProductComponentsTab(key) {
        const {
            dataSource:{
             attributes = {}
            } = {}
        } = this.props;

        return (
            <Suspense fallback={ <Loader /> } key={ key }>
                <ExpandableContent
                    // show placeholder if the details are not loaded
                    heading={ attributes?.components?.attribute_label }
                    mix={ { block: 'ProductInformation', elem: 'Content' } }
                >
                    <div className="ProductInformation">
                        <div className="ProductInformation-Description">
                            <Html content={ attributes?.components?.attribute_value } /></div>
                    </div>
                </ExpandableContent>
            </Suspense>
        );
    }

    renderProductCompositionTab(key) {
        const {
            dataSource:{
                attributes = {}
            } = {}
        } = this.props;

        return (
            <Suspense fallback={ <Loader /> } key={ key }>
                <ExpandableContent
                    // show placeholder if the details are not loaded
                    heading={ attributes.composition.attribute_label }
                    mix={ { block: 'ProductInformation', elem: 'Content' } }
                >
                    <div className="ProductInformation">
                        <div className="ProductInformation-Description"><Html content={ attributes.composition.attribute_value } /></div>
                    </div>
                </ExpandableContent>
            </Suspense>
        );
    }

    renderProductInstructionsTab(key) {
        const {
            dataSource:{
                attributes = {}
            } = {}
        } = this.props;

        return (
            <Suspense fallback={ <Loader /> } key={ key }>
                <ExpandableContent
                    // show placeholder if the details are not loaded
                    heading={ attributes.instructions.attribute_label }
                    mix={ { block: 'ProductInformation', elem: 'Content' } }
                >
                    <div className="ProductInformation">
                        <div className="ProductInformation-Description"><Html content={ attributes.instructions.attribute_value } /></div>
                    </div>
                </ExpandableContent>
            </Suspense>
        );
    }

    renderProductInformationAndAttributesTab(key) {
        const {
            dataSource,
            parameters,
            areDetailsLoaded,
            pdpShareSectionTop,
            setPdpShareSectionTop,
            productOrVariant
        } = this.props;

        return (
            <Suspense fallback={ <Loader /> } key={ key }>
                <ExpandableContent
                    // show placeholder if the details are not loaded
                    heading={ __('PRODUCT DETAILS') }
                    mix={ { block: 'ProductInformation', elem: 'Content' } }
                >
                    <div className="ProductTabs-ContentColumns">
                        <div className="ProductTabs-ContentColumn">
                            <ProductInformation
                                product={ { ...dataSource, parameters } }
                                areDetailsLoaded={ areDetailsLoaded }
                                key={ key }
                                pdpShareSectionTop={pdpShareSectionTop}
                                setPdpShareSectionTop={setPdpShareSectionTop}
                            />
                            <ProductAttributes
                                isProductInformation
                                productOrVariant={ productOrVariant }
                                product={ { ...dataSource, parameters } }
                                areDetailsLoaded={ areDetailsLoaded }
                                key={ key }
                            />
                        </div>
                        <div className="ProductTabs-ContentColumn ProductTabs-ContentColumn_share">
                            { this.renderTabShareSection() }
                        </div>
                    </div>
                </ExpandableContent>
            </Suspense>
        );
    }

    renderTabShareSection(){
        const {
            pdpShareSectionTop,
            dataSource: { id, image } = {}
        } = this.props;

        let image_url = '';

        if (!id) {
            return null;
        }

        if(image) {
            image_url = image.url;
        }

        return(
            <div block="ProductTabsShare" elem="container" style={{'marginTop': pdpShareSectionTop}}>
                <div block="ProductTabsShare" elem="title">{ __('Share') }</div>
                <div block="ProductActionsShare" elem="container">
                    <FacebookShareButton url={window.location.href}>
                        <UniversalIcon
                            src={media('facebook.png', WYSIWYG_MEDIA)}
                            alt="FacebookIcon"
                        />
                    </FacebookShareButton>
                    <TwitterShareButton url={window.location.href}>
                        <UniversalIcon
                            src={media('x-logo-black-smallb.png', WYSIWYG_MEDIA)}
                            alt="TwitterIcon"
                        />
                    </TwitterShareButton>
                    <PinterestShareButton url={window.location.href} media={image_url}>
                        <UniversalIcon
                            src={media('pinterest.png', WYSIWYG_MEDIA)}
                            alt="PinterestIcon"
                        />
                    </PinterestShareButton>
                </div>
            </div>
        );
    }

    renderShippingReturnsTab(key) {
        return (
            <Suspense fallback={ <Loader /> } key={ key }>
                <ExpandableContent
                    // show placeholder if the details are not loaded
                    heading={ __('SHIPPING & RETURNS') }
                    mix={ { block: 'ShippingReturns', elem: 'Content' } }
                >
                    <CmsBlock key={ key } identifier={ 'pdp_shipping_returns' } />
                </ExpandableContent>
            </Suspense>
        );
    }

    renderProductPageContent() {
        const {
            configurableVariantIndex,
            parameters,
            getLink,
            dataSource,
            dataSource: { type_id } = {},
            updateConfigurableVariant,
            productOrVariant,
            areDetailsLoaded,
            getSelectedCustomizableOptions,
            productOptionsData,
            setBundlePrice,
            selectedBundlePrice,
            selectedInitialBundlePrice,
            selectedBundlePriceExclTax,
            setLinkedDownloadables,
            setLinkedDownloadablesPrice,
            selectedLinkPrice,
            brand
        } = this.props;

        return (
            <>
                {
                    type_id !== 'amgiftcard' ?
                        <Suspense fallback={ <Loader /> }>
                            <ProductGallery
                                product={ productOrVariant }
                                areDetailsLoaded={ areDetailsLoaded }
                            />
                        </Suspense>:
                        ''
                }
                <ProductActions
                    getLink={ getLink }
                    updateConfigurableVariant={ updateConfigurableVariant }
                    product={ dataSource }
                    productOrVariant={ productOrVariant }
                    parameters={ parameters }
                    areDetailsLoaded={ areDetailsLoaded }
                    configurableVariantIndex={ configurableVariantIndex }
                    getSelectedCustomizableOptions={ getSelectedCustomizableOptions }
                    productOptionsData={ productOptionsData }
                    setBundlePrice={ setBundlePrice }
                    selectedBundlePrice={ selectedBundlePrice }
                    selectedInitialBundlePrice={ selectedInitialBundlePrice }
                    selectedBundlePriceExclTax={ selectedBundlePriceExclTax }
                    setLinkedDownloadables={ setLinkedDownloadables }
                    setLinkedDownloadablesPrice={ setLinkedDownloadablesPrice }
                    selectedLinkPrice={ selectedLinkPrice }
                    reviewsRef={ reviewsRef }
                    autoRelatedRef={ autoRelatedRef }
                    brand={ brand }
                />
            </>
        );
    }

    renderProductReviewsTab() {
        const {
            dataSource,
            areDetailsLoaded
        } = this.props;

        return (
            <>
                <div className="ProductReviews-Before" ref={reviewsRef}></div>
                <ProductReviews
                    product={ dataSource }
                    areDetailsLoaded={ areDetailsLoaded }
                />
            </>
        );
    }

    renderAdditionalSections() {
        const {
            areDetailsLoaded,
            selectPdpProductTab,
            isUpSellActive,
            dataSource:{
                attributes = {}
            } = {}
        } = this.props;

        let brand = attributes !== undefined
        && attributes.brand !== undefined
        && attributes.brand.attribute_options[attributes.brand.attribute_value] !== undefined ?
            attributes.brand.attribute_options[attributes.brand.attribute_value].label : null;

        if(brand !== null && brand === BRAND_CHANEL) {
            return (
                <>
                    { this.renderCustomizableOptions() }
                    { this.renderProductTabs() }
                    <div className="PdpProductTabs">
                        <div className="ContentWrapper">
                            <ProductLinks
                                isProductPage
                                linkType={ UPSELL }
                                title={ __('You might also like') }
                                areDetailsLoaded={ areDetailsLoaded }
                                selectPdpProductTab={ selectPdpProductTab }
                                pdpProductTab={ isUpSellActive }
                            />
                        </div>
                    </div>
                </>
            );
        }

        return (
            <>
                { this.renderCustomizableOptions() }
                { this.renderProductTabs() }
                { this.renderProductReviewsTab() }
                { this.renderNonTabAutoRelatedProducts() }
                <div className="AutoRelated-Before" ref={autoRelatedRef}></div>
                <div className="PdpProductTabs">
                    <div className="ContentWrapper">
                        { this.renderPdpProductTabs() }
                    </div>
                </div>
                { brand === BRAND_DIOR ? null : <RecentlyViewedWidget areDetailsLoaded= { areDetailsLoaded } /> }
            </>
        );
    }

    renderPdpProductTabs() {
        const {
            areDetailsLoaded,
            selectPdpProductTab,
            isUpSellActive
        } = this.props;

        return (
            <>
                <ProductLinks
                    isProductPage
                    linkType={ UPSELL }
                    title={ __('You might also like') }
                    areDetailsLoaded={ areDetailsLoaded }
                    selectPdpProductTab={ selectPdpProductTab }
                    pdpProductTab={ isUpSellActive }
                />
            </>
        );
    }

    renderNonTabAutoRelatedProducts(){
        return(
            <div className="renderNonTabAutoRelatedProducts"></div>
        );
    }

    renderBrandTitle() {
        const {
            dataSource:{
             attributes = {}
             } = {},
            brand: {
             slider_image,
             url_alias
            } = {}
        } = this.props;

        let brand = attributes !== undefined
            && attributes.brand !== undefined
            && attributes.brand.attribute_options[attributes.brand.attribute_value] !== undefined ?
            attributes.brand.attribute_options[attributes.brand.attribute_value].label : null;

        if(brand !== null && (brand === BRAND_CHANEL || brand === BRAND_DIOR || brand === BRAND_CHRISTIAN_DIOR)) {
            return (
                <ContentWrapper
                    wrapperMix={ { block: 'ProductActions', elem: 'BrandWrapper' } }
                    label={ __('Product Brand') }
                >
                    {/* <meta itemProp="brand" content={ brand } /> */}
                    <h4 block="ProductActions" elem="Brand" 
                    // itemProp="brand" 
                    className={brand}>
                        <Link to={ appendWithStoreCode('/'+url_alias) }>
                            { slider_image ?
                                <UniversalIcon
                                    src={media(slider_image,'amasty/shopby/option_images/slider/')}
                                    alt="BrandIcon"
                                />
                                :
                                <TextPlaceholder content={ brand } />
                            }
                        </Link>
                    </h4>
                </ContentWrapper>
            );
        }

        return null
    }

    renderBrandFooter() {
        const {
            dataSource: {
                attributes = {}
            } = {}
        } = this.props;

        let brand = attributes !== undefined
        && attributes.brand !== undefined
        && attributes.brand.attribute_options[attributes.brand.attribute_value] !== undefined ?
            attributes.brand.attribute_options[attributes.brand.attribute_value].label : null;

        if(brand !== null && brand === BRAND_CHANEL) {
            return (
                <ContentWrapper
                    wrapperMix={ { block: 'ProductActions', elem: 'BrandWrapper' } }
                    label={ __('Product Brand') }
                >
                    <meta 
                    // itemProp="brand" 
                    content={ brand } />
                    <h4 block="ProductActions" 
                    elem="Brand" 
                    // itemProp="brand" 
                    className={`ProductActions-Footer ${brand}`}>
                        <UniversalIcon
                            src={media('chanel-authorized-retailers-logo-chanelauthorizedlogo.png', WYSIWYG_MEDIA)}
                            alt="BrandResellerIcon"
                        />
                    </h4>
                </ContentWrapper>
            );
        }

        if (brand !== null && (brand === BRAND_DIOR || brand === BRAND_CHRISTIAN_DIOR))
            return (
                <ContentWrapper
                    wrapperMix={{ block: 'ProductActions', elem: 'BrandWrapper' }}
                    label={__('Product Brand')}
                >
                    <meta
                        // itemProp="brand" 
                        content={brand} />
                    <h4 block="ProductActions"
                        elem="Brand"
                        className={`ProductActions-Footer ${brand} reseller`}>
                        <UniversalIcon
                            src={media('dior-authorized-retailer-GR.png', WYSIWYG_MEDIA)}
                            alt="Dior Reseller icon"
                        />
                    </h4>
                </ContentWrapper>
            );

        return null
    }

    render() {
        const {
            dataSource:{
                attributes,
                type_id
            } = {}
        } = this.props;

        let brand = attributes !== undefined
        && attributes.brand !== undefined
        && attributes.brand.attribute_options[attributes.brand.attribute_value] !== undefined ?
            attributes.brand.attribute_options[attributes.brand.attribute_value].label : null;

        let brandChanelPdp = document.getElementById('Breadcrumbs-List');

        if(brand === BRAND_CHANEL && brandChanelPdp) {
            brandChanelPdp.style.fontFamily = '"Arial", sans-serif';
        }

        if(brand === BRAND_DIOR || brand === BRAND_CHRISTIAN_DIOR || brand === BRAND_CHANEL) {
            return (
                <NoMatchHandler>
                    <ChanelContent
                        brand={brand}
                    />
                    <main
                        block="ProductPage"
                        aria-label="Product page"
                        itemScope
                        // itemType="http://schema.org/Product"
                        className={ type_id+' '+brand }
                    >
                        { this.renderBrandTitle() }
                        <ContentWrapper
                            wrapperMix={ { block: 'ProductPage', elem: 'Wrapper' } }
                            label={ __('Main product details') }
                        >
                            { this.renderProductPageContent() }
                        </ContentWrapper>
                        { type_id !== 'amgiftcard' ?
                            <>
                                { this.renderAdditionalSections() }
                                { this.renderReviewPopup() }
                            </>
                            : ""
                        }
                        { this.renderBrandFooter() }
                    </main>
                </NoMatchHandler>
            );
        }

        return (
            <NoMatchHandler>
                <ChanelContent
                    brand={brand}
                />
                <main
                    block="ProductPage"
                    aria-label="Product page"
                    itemScope
                    // itemType="http://schema.org/Product"
                    className={ type_id }
                >
                    { this.renderBrandTitle() }
                    <ContentWrapper
                        wrapperMix={ { block: 'ProductPage', elem: 'Wrapper' } }
                        label={ __('Main product details') }
                    >
                        { this.renderProductPageContent() }
                    </ContentWrapper>
                    { type_id !== 'amgiftcard' ?
                        <>
                            { this.renderAdditionalSections() }
                            { this.renderReviewPopup() }
                        </>
                        : ""
                    }
                </main>
            </NoMatchHandler>
        );
    }
}

export default ProductPage;
